import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Trash2, RotateCcw, CheckCircle2, Eye } from 'lucide-react';
import api from '../lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import { VoicemailAudioPlayer } from './VoicemailAudioPlayer';

// v3.21-1837 · Voicemail management: assign/reassign, Reviewed vs Follow-up Completed, soft delete + restore.
const STATUS_STYLE = {
  new: ['New', 'bg-amber-50 text-amber-700'],
  reviewed: ['Reviewed', 'bg-sky-50 text-sky-700'],
  follow_up_completed: ['Follow-up Completed', 'bg-emerald-50 text-emerald-700'],
};
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : '--');
const errMsg = (e, fallback) => e?.response?.data?.detail || fallback;

const StatusBadge = ({ vm }) => {
  const [label, cls] = STATUS_STYLE[vm.status] || STATUS_STYLE.new;
  return (
    <div className="flex flex-col gap-0.5">
      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full w-fit ${cls}`} data-testid={`vm-status-${vm.id}`}>{label}</span>
      {vm.follow_up_task_status && (
        <span className="text-[10px] text-slate-400" data-testid={`vm-task-status-${vm.id}`}>
          Task: {vm.follow_up_task_status}{vm.follow_up_task_assignee ? ` · ${vm.follow_up_task_assignee}` : ''}
        </span>
      )}
    </div>
  );
};

const AssignCell = ({ vm, canAssign, assignees, onAssign }) => {
  if (!canAssign || vm.deleted_at) {
    return vm.assigned_user_name
      ? <span className="text-xs font-medium text-indigo-700 bg-indigo-50 px-1.5 py-0.5 rounded" data-testid={`vm-owner-${vm.id}`}>{vm.assigned_user_name}</span>
      : <span className="text-xs text-slate-400 italic" data-testid={`vm-owner-${vm.id}`}>Unassigned</span>;
  }
  return (
    <Select value={vm.rep_id || ''} onValueChange={(v) => onAssign(vm, v)}>
      <SelectTrigger className="h-7 w-[150px] text-xs" data-testid={`vm-assign-${vm.id}`}>
        <SelectValue placeholder="Unassigned" />
      </SelectTrigger>
      <SelectContent>
        {assignees.map((u) => (
          <SelectItem key={u.id} value={u.id} className="text-xs" data-testid={`vm-assign-option-${vm.id}-${u.id}`}>{u.full_name}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

const RowActions = ({ vm, perms, onStatus, onDelete, onRestore }) => {
  const btn = 'inline-flex items-center gap-1 text-[10px] font-medium transition-colors';
  if (vm.deleted_at) {
    return (
      <div className="flex flex-col gap-0.5">
        <span className="text-[10px] text-slate-400">Restorable until {fmtDate(vm.restore_until)}</span>
        {perms.can_restore && (
          <button onClick={() => onRestore(vm)} className={`${btn} text-blue-600 hover:text-blue-800`} data-testid={`vm-restore-${vm.id}`}>
            <RotateCcw className="h-3 w-3" /> Restore
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3 flex-wrap">
      {vm.has_recording
        ? <VoicemailAudioPlayer voicemailId={vm.id} initialDuration={vm.duration} />
        : <span className="text-[10px] text-slate-400 italic" data-testid={`vm-no-recording-${vm.id}`}>No recording</span>}
      {vm.status === 'new' && (
        <button onClick={() => onStatus(vm, 'reviewed')} className={`${btn} text-sky-600 hover:text-sky-800`} data-testid={`vm-review-${vm.id}`}>
          <Eye className="h-3 w-3" /> Mark Reviewed
        </button>
      )}
      {vm.status !== 'follow_up_completed' && (
        <button onClick={() => onStatus(vm, 'follow_up_completed')} className={`${btn} text-emerald-600 hover:text-emerald-800`} data-testid={`vm-complete-${vm.id}`}>
          <CheckCircle2 className="h-3 w-3" /> Follow-up Completed
        </button>
      )}
      {perms.can_delete && (
        <button onClick={() => onDelete(vm)} className={`${btn} text-red-500 hover:text-red-700`} data-testid={`vm-delete-${vm.id}`}>
          <Trash2 className="h-3 w-3" /> Delete
        </button>
      )}
    </div>
  );
};

const DeleteDialog = ({ vm, onCancel, onConfirm }) => (
  <AlertDialog open={!!vm} onOpenChange={(o) => { if (!o) onCancel(); }}>
    <AlertDialogContent data-testid="vm-delete-dialog">
      <AlertDialogHeader>
        <AlertDialogTitle>Delete this voicemail?</AlertDialogTitle>
        <AlertDialogDescription>
          The voicemail from {vm?.client_name || vm?.caller || 'this caller'} moves to Deleted. An Administrator can restore it
          within 30 days. The recording and transcript are kept.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="vm-delete-cancel">Cancel</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} className="bg-red-600 hover:bg-red-700" data-testid="vm-delete-confirm">Delete</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const VoicemailsPanel = ({ onTotal }) => {
  const [view, setView] = useState('all');
  const [rows, setRows] = useState([]);
  const [perms, setPerms] = useState({});
  const [assignees, setAssignees] = useState([]);
  const [denied, setDenied] = useState('');
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = view === 'mine' ? { mine: 'true' } : view === 'deleted' ? { view: 'deleted' } : {};
      const { data } = await api.get('/telephony/voicemails', { params });
      setRows(data.voicemails || []); setPerms(data.permissions || {}); setDenied('');
      if (view !== 'deleted') onTotal?.(data.total || 0);
    } catch (e) {
      if (e?.response?.status === 403) setDenied(errMsg(e, 'Access denied.'));
      else toast.error(errMsg(e, 'Failed to load voicemails'));
    }
  }, [view, onTotal]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!perms.can_assign) return;
    api.get('/telephony/voicemails/assignees').then(({ data }) => setAssignees(data.assignees || [])).catch(() => {});
  }, [perms.can_assign]);

  const act = async (fn, okMsg) => {
    try { await fn(); toast.success(okMsg); await load(); } catch (e) { toast.error(errMsg(e, 'Action failed')); }
  };
  const onAssign = (vm, repId) => act(() => api.put(`/telephony/voicemails/${vm.id}/assign`, { rep_id: repId }), 'Voicemail assigned');
  const onStatus = (vm, status) => act(() => api.put(`/telephony/voicemails/${vm.id}`, { status }), 'Status updated');
  const onRestore = (vm) => act(() => api.post(`/telephony/voicemails/${vm.id}/restore`), 'Voicemail restored');
  const confirmDelete = () => { const vm = toDelete; setToDelete(null); act(() => api.delete(`/telephony/voicemails/${vm.id}`), 'Voicemail deleted'); };

  if (denied) {
    return <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-sm text-slate-500" data-testid="vm-access-denied">{denied}</div>;
  }
  return (
    <div className="bg-white rounded-xl border border-slate-200">
      <div className="p-4 border-b border-slate-100">
        <Select value={view} onValueChange={setView}>
          <SelectTrigger className="w-[180px] h-8 text-xs" data-testid="vm-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Voicemails</SelectItem>
            <SelectItem value="mine">My Voicemails</SelectItem>
            {perms.can_delete && <SelectItem value="deleted" data-testid="vm-filter-deleted">Deleted (30-day restore)</SelectItem>}
          </SelectContent>
        </Select>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 text-left">
              {['Caller', 'Assigned To', 'Duration', 'Transcription', 'Status', 'Date', 'Actions'].map((h) => (
                <th key={h} className="px-4 py-2.5 text-xs font-medium text-slate-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400 text-sm" data-testid="vm-empty">No voicemails</td></tr>
            ) : rows.map((vm) => (
              <tr key={vm.id} className="border-b border-slate-50 hover:bg-slate-50/50 align-top" data-testid={`voicemail-${vm.id}`}>
                <td className="px-4 py-2.5 text-xs text-slate-700">{vm.client_name || vm.caller || '--'}</td>
                <td className="px-4 py-2.5"><AssignCell vm={vm} canAssign={perms.can_assign} assignees={assignees} onAssign={onAssign} /></td>
                <td className="px-4 py-2.5 text-xs text-slate-500">{vm.duration ? `${vm.duration}s` : '--'}</td>
                <td className="px-4 py-2.5 text-xs text-slate-600 max-w-[220px]" title={vm.transcription || ''} data-testid={`vm-transcript-${vm.id}`}>
                  <div className="line-clamp-2">
                    {vm.transcription || <span className="text-slate-300 italic">{vm.transcription_status && vm.transcription_status !== 'in-progress' ? 'No transcript available' : 'Pending'}</span>}
                  </div>
                </td>
                <td className="px-4 py-2.5"><StatusBadge vm={vm} /></td>
                <td className="px-4 py-2.5 text-xs text-slate-500">{fmtDate(vm.created_at)}</td>
                <td className="px-4 py-2.5"><RowActions vm={vm} perms={perms} onStatus={onStatus} onDelete={setToDelete} onRestore={onRestore} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <DeleteDialog vm={toDelete} onCancel={() => setToDelete(null)} onConfirm={confirmDelete} />
    </div>
  );
};

export default VoicemailsPanel;
